import React, { useState, useRef, useEffect } from 'react';
import { FilterIcon, Clock, X } from 'lucide-react';
import './FilterComponent.css';

const FilterComponent = ({ onFilterChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState({
    eta: null,
    strain: [],
    category: [],
    priceRange: [0, 200]
  });
  const dropdownRef = useRef(null);

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Send filter changes to parent component
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(filters);
    }
  }, [filters, onFilterChange]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleEtaFilter = (eta) => {
    setFilters(prev => ({
      ...prev,
      eta: prev.eta === eta ? null : eta
    }));
  };

  const handleStrainFilter = (strain) => {
    setFilters(prev => {
      const updatedStrains = prev.strain.includes(strain)
        ? prev.strain.filter(s => s !== strain)
        : [...prev.strain, strain];
      
      return {
        ...prev,
        strain: updatedStrains
      };
    });
  };

  const handleCategoryFilter = (category) => {
    setFilters(prev => {
      const updatedCategories = prev.category.includes(category)
        ? prev.category.filter(c => c !== category)
        : [...prev.category, category];
      
      return {
        ...prev,
        category: updatedCategories
      };
    });
  };

  const handlePriceRangeChange = (event, index) => {
    const value = parseInt(event.target.value, 10);
    setFilters(prev => {
      const newRange = [...prev.priceRange];
      newRange[index] = value;
      return {
        ...prev,
        priceRange: newRange
      };
    });
  };

  const clearFilters = () => {
    setFilters({
      eta: null,
      strain: [],
      category: [],
      priceRange: [0, 200]
    });
  };

  // Calculate if any filters are active
  const hasActiveFilters = 
    filters.eta !== null || 
    filters.strain.length > 0 || 
    filters.category.length > 0 || 
    filters.priceRange[0] > 0 || 
    filters.priceRange[1] < 200;

  return (
    <div className="filter-container" ref={dropdownRef}>
      <div 
        className={`filter-button ${hasActiveFilters ? 'active' : ''}`}
        onClick={toggleDropdown}
      >
        <FilterIcon 
          size={18}
          className="filter-icon"
          strokeWidth={2.5}
        />
        {hasActiveFilters && <span className="filter-badge"></span>}
      </div>

      {isOpen && (
        <div className="filter-dropdown">
          <div className="filter-header">
            <h5>Filter Options</h5>
            {hasActiveFilters && (
              <button className="clear-filters" onClick={clearFilters}>
                <X size={14} /> Clear All
              </button>
            )}
          </div>

          {/* ETA Filter */}
          <div className="filter-section">
            <h6>Delivery ETA</h6>
            <div className="eta-options">
              <button 
                className={`eta-option ${filters.eta === 'quick' ? 'active' : ''}`}
                onClick={() => handleEtaFilter('quick')}
              >
                <Clock size={14} /> &lt; 30 mins
              </button>
              <button 
                className={`eta-option ${filters.eta === 'medium' ? 'active' : ''}`}
                onClick={() => handleEtaFilter('medium')}
              >
                <Clock size={14} /> ~ 1 hour
              </button>
              <button 
                className={`eta-option ${filters.eta === 'slow' ? 'active' : ''}`}
                onClick={() => handleEtaFilter('slow')}
              >
                <Clock size={14} /> + 2 Hours
              </button>
            </div>
          </div>

          {/* Strain Filter */}
          <div className="filter-section">
            <h6>Strain Type</h6>
            <div className="strain-options">
              <button 
                className={`strain-badge sativa ${filters.strain.includes('sativa') ? 'active' : ''}`}
                onClick={() => handleStrainFilter('sativa')}
              >
                Sativa
              </button>
              <button 
                className={`strain-badge hybrid ${filters.strain.includes('hybrid') ? 'active' : ''}`}
                onClick={() => handleStrainFilter('hybrid')}
              >
                Hybrid
              </button>
              <button 
                className={`strain-badge indica ${filters.strain.includes('indica') ? 'active' : ''}`}
                onClick={() => handleStrainFilter('indica')}
              >
                Indica
              </button>
            </div>
          </div>

          {/* Product Category Filter */}
          <div className="filter-section">
            <h6>Product Category</h6>
            <div className="category-options">
              {['Flower', 'Pre-roll', 'Edible', 'Vape', 'Concentrate', 'Tincture', 'Gear'].map(category => (
                <button 
                  key={category}
                  className={`category-badge ${filters.category.includes(category.toLowerCase()) ? 'active' : ''}`}
                  onClick={() => handleCategoryFilter(category.toLowerCase())}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Price Range Filter */}
          <div className="filter-section">
            <h6>Price Range</h6>
            <div className="price-slider-container">
              <div className="price-range-display">
                <span>${filters.priceRange[0]}</span>
                <span>${filters.priceRange[1]}</span>
              </div>
              <input 
                type="range"
                min="0"
                max="200"
                value={filters.priceRange[0]}
                onChange={(e) => handlePriceRangeChange(e, 0)}
                className="price-slider min-slider"
              />
              <input 
                type="range"
                min="0"
                max="200"
                value={filters.priceRange[1]}
                onChange={(e) => handlePriceRangeChange(e, 1)}
                className="price-slider max-slider"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterComponent;